import { useEffect, useRef } from 'react'
import { useAuthStore } from '../../stores/auth'
import { useNotificationStore } from '../../stores/notifications'
import { useBrowserNotifications } from '../../hooks/useBrowserNotifications'
import { notificationsApi } from '../../api/endpoints'

const POLL_INTERVAL_MS = 5_000

/**
 * Pure-effect component that polls for new notifications and fires browser
 * push notifications for each one.  Renders nothing (returns null).
 *
 * Mount this once inside AppLayout so it runs for every authenticated page.
 */
export default function NotificationPoller() {
  const { isAuthenticated } = useAuthStore()
  const { lastSeenId, setLastSeenId, prefs } = useNotificationStore()
  const { notify, permission } = useBrowserNotifications()

  // Keep mutable refs so the interval callback always sees the latest values
  // without needing to clear/reset the interval on every state change.
  const lastSeenIdRef = useRef(lastSeenId)
  const prefsRef = useRef(prefs)
  const notifyRef = useRef(notify)
  const permissionRef = useRef(permission)
  const initializedRef = useRef(false)

  useEffect(() => { lastSeenIdRef.current = lastSeenId }, [lastSeenId])
  useEffect(() => { prefsRef.current = prefs }, [prefs])
  useEffect(() => { notifyRef.current = notify }, [notify])
  useEffect(() => { permissionRef.current = permission }, [permission])

  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    // Build the list of notification types the user has enabled.
    function enabledTypes(): string[] {
      const types: string[] = []
      const p = prefsRef.current
      if (p.task_assigned) types.push('task_assigned')
      if (p.lead_assigned) types.push('lead_assigned')
      if (p.task_overdue) types.push('task_overdue')
      return types
    }

    async function poll() {
      const types = enabledTypes()
      if (types.length === 0) {
        return
      }

      try {
        const data = await notificationsApi.poll({
          since_id: lastSeenIdRef.current,
          types,
        })

        if (!data.notifications || data.notifications.length === 0) {
          // Even with no notifications, the server may return a max_id we
          // should track so subsequent polls are efficient.
          if (data.max_id && data.max_id > lastSeenIdRef.current) {
            lastSeenIdRef.current = data.max_id
            setLastSeenId(data.max_id)
          }
          return
        }

        // On the very first successful response, just seed lastSeenId so we
        // don't fire notifications for everything that already existed.
        if (!initializedRef.current) {
          initializedRef.current = true
          if (data.max_id) {
            lastSeenIdRef.current = data.max_id
            setLastSeenId(data.max_id)
          }
          return
        }

        // Fire a browser notification for each new item.
        for (const n of data.notifications) {
          const nType = n.notification_type as string | undefined
          const title = (n.title as string) || 'New notification'
          const body = (n.body as string) || ''
          const entityId = n.entity_id as number | undefined

          let navigateTo = '/notifications'
          if (nType === 'task_assigned' || nType === 'task_overdue') {
            navigateTo = '/tasks'
          } else if (nType === 'lead_assigned' && entityId) {
            navigateTo = `/leads/${entityId}`
          }

          notifyRef.current(
            (nType as keyof typeof prefsRef.current) || 'task_assigned',
            title,
            body,
            navigateTo,
          )
        }

        // Advance the cursor.
        if (data.max_id && data.max_id > lastSeenIdRef.current) {
          lastSeenIdRef.current = data.max_id
          setLastSeenId(data.max_id)
        }
      } catch {
        // Silently swallow poll errors -- the user should not see transient
        // network hiccups from a background poller.
      }
    }

    // Fire the first poll immediately, then set up the interval.
    poll()
    const id = setInterval(poll, POLL_INTERVAL_MS)
    return () => clearInterval(id)
    // Re-create the interval when authentication state changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  return null
}
